import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface ConsoleErrorTrendProps {
  data: Array<{
    weekStart: string;
    weekLabel: string;
    avgConsoleErrors: number | null;
    issueCount: number;
  }>;
  isLoading?: boolean;
}

export function ConsoleErrorTrendChart({ data, isLoading = false }: ConsoleErrorTrendProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent className="h-80">
          <div className="flex items-center justify-center h-full">
            <Skeleton className="h-64 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Filter to only include weeks with console error data
  const chartData = data
    .filter((d) => d.avgConsoleErrors !== null)
    .map((d) => ({
      ...d,
      avgConsoleErrors: d.avgConsoleErrors ?? 0,
    }));

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Console Error Trends</CardTitle>
          <CardDescription>Average console errors per issue over time</CardDescription>
        </CardHeader>
        <CardContent className="h-80">
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <p className="text-sm">No console error data available</p>
            <p className="text-xs mt-1">Trends will appear after Design Review runs</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="chart-console-error-trend">
      <CardHeader>
        <CardTitle>Console Error Trends</CardTitle>
        <CardDescription>Average console errors per issue over time</CardDescription>
      </CardHeader>
      <CardContent className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="weekLabel" />
            <YAxis domain={[0, 'auto']} />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "0.5rem",
              }}
              formatter={(value: number, name: string, props: any) => [
                `${value.toFixed(1)} avg errors (${props.payload.issueCount} issues)`,
                "",
              ]}
              labelFormatter={(label) => `Week ${label}`}
            />
            <Line
              type="monotone"
              dataKey="avgConsoleErrors"
              stroke="hsl(0, 84%, 60%)"
              strokeWidth={2}
              dot={{ fill: "hsl(0, 84%, 60%)", strokeWidth: 2 }}
              name="Avg Console Errors"
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
